import { z } from "zod";
import { ACTIVITIES, CALENDAR_SKILLS } from "./enums.ts";
export const FOCUS_PRESETS = {
  "25/5": [1500, 300],
  "50/10": [3000, 600],
  "15/3": [900, 180],
} as const;
export const FocusInput = z.strictObject({
  preset: z.enum(["25/5", "50/10", "15/3"]),
  background: z.enum(["ocean", "forest", "night"]).default("ocean"),
});
export const FocusRun = z.object({
  id: z.uuid(),
  preset: FocusInput.shape.preset,
  startedAt: z.string(),
  focusEndsAt: z.string(),
  breakEndsAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  pausedAt: z.string().nullable().default(null),
  extendedSecs: z.number().default(0),
  skippedAt: z.string().nullable().default(null),
  background: z.string(),
});
export type FocusRun = z.infer<typeof FocusRun>;
export const CalendarInput = z
  .strictObject({
    date: z.iso.date(),
    startMin: z.int().min(0).max(1425),
    endMin: z.int().min(15).max(1440),
    title: z.string().trim().min(1).max(150),
    skill: z.enum(CALENDAR_SKILLS),
    done: z.boolean().default(false),
  })
  .refine(
    (x) => x.endMin > x.startMin && x.startMin % 15 === 0 && x.endMin % 15 === 0,
    "Buổi học cần theo bước 15 phút.",
  );
export const CalendarEvent = z.object({
  id: z.uuid(),
  date: z.string(),
  startMin: z.number(),
  endMin: z.number(),
  title: z.string(),
  skill: z.enum(CALENDAR_SKILLS),
  done: z.boolean(),
  updatedAt: z.string(),
});
export type CalendarEvent = z.infer<typeof CalendarEvent>;
export const HabitInput = z.strictObject({
  name: z.string().trim().min(1).max(100),
  icon: z.string().max(10).default("✅"),
  autoActivity: z.enum(ACTIVITIES).nullable().default(null),
});
export const Habit = HabitInput.extend({ id: z.uuid(), checks: z.array(z.string()) });
export type Habit = z.infer<typeof Habit>;
/** Nguồn 🫧 server đang cộng; `reason` khớp `points_ledger.reason`. */
export const POINT_RULES = [
  { reason: "focus", icon: "🍅", label: "Xong một hiệp tập trung", points: 10, isNew: false },
  {
    reason: "daily_goal",
    icon: "🎯",
    label: "Đạt mục tiêu phút trong ngày",
    points: 5,
    isNew: true,
  },
  { reason: "error_group", icon: "📕", label: "Trị xong một nhóm lỗi", points: 15, isNew: true },
  { reason: "streak_day", icon: "🔥", label: "Mỗi ngày giữ chuỗi", points: 1, isNew: true },
  {
    reason: "streak7",
    icon: "🏅",
    label: "Ngày thứ 7, 14, 21… của chuỗi",
    points: 20,
    isNew: true,
  },
] as const;
export type PointReason = (typeof POINT_RULES)[number]["reason"];
/** Một dòng sổ 🫧 (cộng hoặc trừ khi đổi quà). */
export const PointEntry = z.object({
  reason: z.string(),
  delta: z.number(),
  createdAt: z.iso.datetime(),
});
export type PointEntry = z.infer<typeof PointEntry>;
export const Achievements = z.object({
  points: z.number(),
  earned: z.array(z.string()),
  items: z.array(
    z.object({ key: z.string(), name: z.string(), cost: z.number(), owned: z.boolean() }),
  ),
  rounds: z.number(),
  minutes: z.number(),
  days: z.number(),
  /** Số lần trị xong một nhóm lỗi. */
  fixes: z.number().default(0),
});
export type Achievements = z.infer<typeof Achievements>;
export const GoldenHour = z.object({
  hours: z.array(
    z.object({
      hour: z.number(),
      answered: z.number(),
      correct: z.number(),
      activeSecs: z.number(),
    }),
  ),
  best: z.number().nullable(),
});

export type FocusInput = z.infer<typeof FocusInput>;
export type CalendarInput = z.infer<typeof CalendarInput>;
